import { Application } from '../../config'
import { IAvailability, IPaymentMethod, IReservation } from '../../models'
import { IExpeditionStatus } from '../../models/expedition-status'
import { BaseHttpService } from './base'

const { isIframe } = Application
class ReservationService extends BaseHttpService {
  public getReservation = async (id: number): Promise<IReservation> => {
    return this.get(`reservations/${id}`)
  }

  public getReservationStatus = async (id: number): Promise<IExpeditionStatus> => {
    return this.get(`reservations/${id}/expeditionstatus`)
  }

  public getBusPosition = async (id: number): Promise<any> => {
    return this.get(`reservations/${id}/busposition`)
  }

  public getCancelledReservation = async (
    id: number
  ): Promise<IReservation> => {
    return this.get(`reservations/cancelled/${id}`)
  }

  public getReservationWithServices = async (
    id: number
  ): Promise<IReservation> => {
    return this.get(`reservations/${id}/service`)
  }

  public getReservations = async (): Promise<IReservation[]> => {
    return this.get('reservations?size=10&page=1')
  }
  
  public getMoreReservations = async (
    size: number,
    page: number
  ): Promise<IReservation[]> => {
    return this.get(`reservations?size=${size}&page=${page}`)
  }

  public getCancelledReservations = async (): Promise<IReservation[]> => {
    return this.get('reservations/cancelled')
  }

  public getPastReservations = async (size: number, page: number): Promise<IReservation[]> => {
    return this.get(`reservations/history?size=${size}&page=${page}`)
  }

  public getRefundedReservations = async (): Promise<IReservation[]> => {
    return this.get('reservations/refunded')
  }

  public getFaultReservations = async (): Promise<IReservation[]> => {
    return this.get('reservations/faults')
  }

  public postReservation = async (
    availability: IAvailability,
    seats: number,
    prmSeats: number,
    serviceId: number,
    paymentMethodId: string,
    userData?: {
      firstName: string,
      lastName: string,
      email: string,
    },
    isSearchingCoordsInMap?: boolean,
    activePassId?: number,
    paymentMethod?: IPaymentMethod
  ): Promise<void> => {
    const outStop = availability.outStops[0]
    // Door to door stops are not fixed points the backend can look up by id,
    // so they travel with their address and coordinates.
    const exitStop = isSearchingCoordsInMap
      ? {
          id: outStop.id,
          name: outStop.name,
          latitude: outStop.location.geometry.coordinates[1],
          longitude: outStop.location.geometry.coordinates[0]
        }
      : { id: outStop.id }

    const seatsFormated = Array(seats)
      .fill({
        exitStop,
        prm: false
      })
      .concat(
        Array(prmSeats).fill({
          exitStop,
          prm: true
        })
      )

    let body: any = {
      availability: {
        id: availability.id
      },
      beginningStop: {
        id: availability.inStop.id,
        ...(isSearchingCoordsInMap && {
          name: availability.inStop.name,
          latitude: availability.inStop.location.geometry.coordinates[1],
          longitude: availability.inStop.location.geometry.coordinates[0]
        })
      },
      seats: seatsFormated,
      availabilityResponseId: availability.serviceAvailabilityResponseId,
      serviceId: serviceId,
      expeditionId: availability.expeditionId,
      paymentMethodId: paymentMethodId,
      ...(activePassId !== undefined && { activePassId }),
      ...(paymentMethod && { paymentMethod })
    }

    if (userData) {
      body = {
        ...body,
        userCreation: true,
        user: userData
      }
    }

    return await this.post(`reservations${isIframe ? '/user' : ''}`, body)
  }

  public updateReservation = async (
    reservationId: number,
    availability: IAvailability,
    seats: number,
    prmSeats: number,
    serviceId: number,
    paymentMethod?: IPaymentMethod
  ): Promise<void> => {
    const stopId = availability.outStops[0].id
    const seatsFormated = Array(seats)
      .fill({
        exitStop: { id: stopId },
        prm: false
      })
      .concat(
        Array(prmSeats).fill({
          exitStop: { id: stopId },
          prm: true
        })
      )

    await this.put(`reservations/${reservationId}`, {
      availability: {
        id: availability.id
      },
      beginningStop: {
        id: availability.inStop.id
      },
      seats: seatsFormated,
      availabilityResponseId: availability.serviceAvailabilityResponseId,
      serviceId: serviceId,
      expeditionId: availability.expeditionId,
      ...(paymentMethod && { paymentMethod })
    })
  }

  public deleteReservation = async (reservationId: number, comment = ''): Promise<void> => {
    await this.delete(`reservations/${reservationId}`, { comment })
  }

  public postMultipleReservation = async (
    availabilities: IAvailability[],
    seats: number,
    prmSeats: number,
    paymentMethodId: string,
    userData?: {
      firstName: string,
      lastName: string,
      email: string,
      phone: string
    },
    paymentMethod?: IPaymentMethod
  ) => {
    let createReservationBody: any[] = []
    availabilities.map((availability) => {
      const stopId = availability.outStops[0].id
      const seatsFormated = Array(seats)
        .fill({
          exitStop: { id: stopId },
          prm: false
        })
        .concat(
          Array(prmSeats).fill({
            exitStop: { id: stopId },
            prm: true
          })
        )
      
      let body: any = {
        availability: {
          id: availability.id
        },
        beginningStop: {
          id: availability.inStop.id
        },
        seats: seatsFormated,
        availabilityResponseId: availability.serviceAvailabilityResponseId,
        serviceId: availability.serviceId,
        expeditionId: availability.expeditionId,
        paymentMethodId: paymentMethodId,
        ...(paymentMethod && { paymentMethod })
      }
  
      if (userData) {
        body = {
          ...body,
          userCreation: true,
          user: userData
        }
      }
      createReservationBody.push(body)
    })

    return await this.post(`reservations/multiple${isIframe ? '/user' : ''}`, createReservationBody)
  }

  public updateMultipleReservations = async(
    reservationIds: number[],
    availabilities: IAvailability[],
    seats: number,
    prmSeats: number,
    serviceIds: number[],
    paymentMethod?: IPaymentMethod
  ) => {
    let createReservationBody: any[] = []
    availabilities.map((availability) => {
      const stopId = availability.outStops[0].id
      const seatsFormated = Array(seats)
        .fill({
          exitStop: { id: stopId },
          prm: false
        })
        .concat(
          Array(prmSeats).fill({
            exitStop: { id: stopId },
            prm: true
          })
        )
      
      let body: any = {
        id: reservationIds.shift(),
        availability: {
          id: availability.id
        },
        beginningStop: {
          id: availability.inStop.id
        },
        seats: seatsFormated,
        availabilityResponseId: availability.serviceAvailabilityResponseId,
        serviceId: serviceIds.shift(),
        expeditionId: availability.expeditionId,
        ...(paymentMethod && { paymentMethod })
      }

      createReservationBody.push(body)
    })

    return await this.put(`reservations/multiple`, createReservationBody)
  }
}


export default new ReservationService()
